import GridCellEdit from '../../Grid/feature/CellEdit.js';
import GridFeatureManager from '../../Grid/feature/GridFeatureManager.js';
import TransactionalFeature from '../../Scheduler/feature/mixin/TransactionalFeature.js';

/**
 * @module SchedulerPro/feature/CellEdit
 */

/**
 * Extends the {@link Grid.feature.CellEdit} to encapsulate SchedulerPro functionality.
 *
 * This feature is **enabled** by default.
 *
 * @extends Grid/feature/CellEdit
 *
 * @classtype cellEdit
 * @feature
 *
 * @typings Grid.feature.CellEdit -> Grid.feature.GridCellEdit
 */
export default class CellEdit extends TransactionalFeature(GridCellEdit) {
    static $name = 'CellEdit';

    static configurable = {
        stopFeatureTransactionOnCellEditFinish : false
    };

    async startEditing(cellContext = {}) {
        const me = this;

        let result;

        // If any transactional feature is active at the moment, block editing start. Edit should be finished first.
        if (me.client.isTransactionalFeatureActive && !me._hasFeatureTransactionCapture) {
            result = false;
        }
        else {
            // First wait for queue to finish to start new transaction for the current feature
            await me.startFeatureTransaction();

            result = await super.startEditing(cellContext);

            if (!result) {
                await me.finishFeatureTransaction();
            }
        }

        return result;
    }

    async finishEditing(event) {
        const result = await super.finishEditing(event);

        if (result) {
            await this.finishFeatureTransaction?.();
        }

        return result;
    }

    // This is an override of the corresponding method in the Grid CellEditor feature. While revisions
    // are not supported in Grid, it is simpler to override entire method than to try to hook into the
    // lifecycle and manage state of the feature transaction.
    async finishAndEditNextRow(event, previous = false) {
        const
            me                 = this,
            { grid }           = me,
            { record, column } = me.editorContext;

        let nextCell;

        // Capture transaction to keep single revision for multiple actions
        me.captureFeatureTransaction();

        if (await me.finishEditing(event)) {
            // Might be destroyed during the async operation
            if (me.isDestroyed) {
                return;
            }

            // Finalizing might have been blocked by an invalid value
            if (!me.isEditing) {
                // Move to previous
                if (previous) {
                    nextCell = grid.internalNextPrevRow(false, true, false);
                    if (!nextCell && grid.splitFrom) {
                        const otherGrid = grid.splitFrom;

                        await otherGrid.startEditing({
                            id       : otherGrid.store.last.id,
                            columnId : column.id
                        });
                    }
                }
                // Move to next
                else {
                    // If we are at the last editable cell, optionally add a new row
                    if (record === grid.store.last) {
                        if (grid.features.lockRows?.enabled) {
                            const otherGrid = grid.features.lockRows.subViews[0];

                            if (otherGrid.store.count > 0) {
                                await otherGrid.startEditing({
                                    id       : otherGrid.store.first.id,
                                    columnId : column.id
                                });
                            }
                        }
                        else if (me.addNewAtEnd) {
                            await me.doAddNewAtEnd();
                        }
                    }

                    if (!me.isDestroyed) {
                        nextCell = grid.internalNextPrevRow(true, true);
                    }
                }

                // If we have moved, and we are configure to edit the next cell on Enter key...
                if (nextCell && me.editNextOnEnterPress) {
                    // If editing cannot be started, release the transaction and finish it
                    if (!(await me.startEditing(nextCell)) && !me.isDestroying) {
                        me.releaseFeatureTransaction();
                        await me.finishFeatureTransaction();
                    }
                }
                // Next edit did not start and previous has successfully finished - finish the transaction
                else if (!me.isDestroying) {
                    me.releaseFeatureTransaction();
                    await me.finishFeatureTransaction();
                }
            }
        }

        if (!me.isDestroying) {
            me.releaseFeatureTransaction();
        }
    }

    cancelEditing(silent = false, triggeredByEvent) {
        this.rejectFeatureTransaction();

        super.cancelEditing(silent, triggeredByEvent);
    }
}

GridFeatureManager.registerFeature(CellEdit, true, 'SchedulerPro');
